/*
  DIREVULF — product page behaviour.

  Responsibilities:
    - resolve the selected variant from the option radios
    - keep price, availability, the add-to-cart button and the URL in sync
    - swap the gallery to a variant's featured media
    - thumbnail navigation

  Progressive enhancement contract:
    Without JavaScript the <noscript> variant <select name="id"> inside the form
    is what the browser submits, and the form posts to /cart normally. This file
    injects the hidden id input only when it runs, so the two never collide.

  Nothing here invents stock levels, urgency or demand. Availability comes from
  Shopify's own variant data and nowhere else.
*/
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* Shopify's money format is a template with ONE of eight placeholders, and
     which one it is decides the decimals and the separators. The earlier
     version of this function only understood {{ amount }}, so on a store whose
     format is the very common "₹{{amount_no_decimals}}" it substituted
     nothing and printed the template at the shopper — a real defect found on
     every product page during the Phase 9 runtime check.

     This is Shopify's own published formatter, so a price rendered here reads
     exactly as the same price rendered by the Liquid `money` filter. */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (typeof precision !== 'number') precision = 2;
    thousands = typeof thousands === 'string' ? thousands : ',';
    decimal = typeof decimal === 'string' ? decimal : '.';
    if (isNaN(cents) || cents === null) return '0';
    var fixed = (cents / 100.0).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function money(cents, format) {
    var template = format || '{{amount}}';
    var found = template.match(MONEY_PLACEHOLDER);
    if (!found) return formatWithDelimiters(cents, 2);
    var value;
    switch (found[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' '); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }
    return template.replace(MONEY_PLACEHOLDER, value);
  }

  /* The handle of the product this page is showing, taken from the URL Liquid
     already wrote into the wrapper. Nothing else about the product is stored. */
  function handleOf(root) {
    var url = root.getAttribute('data-product-url') || '';
    var parts = url.split('/products/');
    return parts.length > 1 ? parts[1].split('?')[0] : '';
  }

  function initProduct(root) {
    /* Remember, on this device only, that this product was opened — so the
       recently viewed rail can fetch the card back from Shopify later and show
       the product as it is then, not as it was now. recent.js owns the store;
       this is the only place that adds to it, and it adds a handle. */
    if (window.DirevulfRecent) {
      var handle = handleOf(root);
      if (handle) window.DirevulfRecent.record(handle);
    }

    var dataNode = $('[data-dv-variant-data]', root);
    if (!dataNode) return;

    var variants;
    try { variants = JSON.parse(dataNode.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    var form = $('[data-dv-product-form]', root);
    if (!form) return;

    var strings = {};
    var stringNode = $('[data-dv-product-strings]', root);
    if (stringNode) { try { strings = JSON.parse(stringNode.textContent); } catch (e) { strings = {}; } }

    var idInput = $('input[name="id"][data-dv-variant-id]', form);
    if (!idInput) {
      idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.setAttribute('data-dv-variant-id', '');
      form.appendChild(idInput);
    }

    var priceHost = $('[data-dv-price]', root);
    var submit = $('[data-dv-add]', root);
    var submitText = submit ? $('[data-dv-add-text]', submit) : null;
    var radios = $$('.dv-size__input', root);

    /* PICTURE OPTION. On a product whose "Shop by Variant" strip shows one
       picture per value of an option (snippets/product-looks.liquid, e.g.
       Edge on a desk mat), that option's row is hidden and marked
       data-dv-picture-option, but its radios are still what this file reads.
       The tiles check those radios, and the other rows are reduced to the
       values that exist with the chosen picture value. A product without the
       marker has picIndex -1, and none of the code below that depends on it
       changes anything. */
    var groups = $$('.dv-variants__group', root);
    var picGroup = $('.dv-variants__group[data-dv-picture-option]', root);
    var picIndex = picGroup ? groups.indexOf(picGroup) : -1;
    var picTiles = picIndex >= 0 ? $$('[data-dv-looks-option] [data-dv-look-value]', root) : [];
    var booting = true;

    function selectedOptions() {
      var values = [];
      $$('.dv-variants__group', root).forEach(function (group) {
        var checked = $('.dv-size__input:checked', group);
        values.push(checked ? checked.value : null);
      });
      return values;
    }

    function match(values) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] === null) { ok = false; break; }
          if (v.options[j] !== values[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    /* Mark option values that lead to no purchasable variant. Derived from real
       inventory only — the control stays selectable so the shopper can still
       see the combination, it is simply labelled sold out. */
    function markAvailability() {
      var values = selectedOptions();
      $$('.dv-variants__group', root).forEach(function (group, gi) {
        $$('.dv-size__input', group).forEach(function (input) {
          var probe = values.slice();
          probe[gi] = input.value;
          var candidate = null;
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i];
            var ok = true;
            for (var j = 0; j < probe.length; j++) {
              if (probe[j] === null) continue;
              if (v.options[j] !== probe[j]) { ok = false; break; }
            }
            if (ok) { candidate = v; break; }
          }
          var label = group.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          /* Hidden by pruneToPicture(): not a sold-out size, no size at all. */
          if (input.hidden) {
            label.classList.remove('is-unavailable');
            label.removeAttribute('data-unavailable');
            return;
          }
          var unavailable = !candidate || !candidate.available;
          label.classList.toggle('is-unavailable', unavailable);
          if (unavailable) label.setAttribute('data-unavailable', strings.sold_out || '');
          else label.removeAttribute('data-unavailable');
        });
      });
    }

    function renderPrice(variant) {
      if (!priceHost) return;
      var fmt = priceHost.getAttribute('data-money-format') || '';
      var now = priceHost.querySelector('[data-dv-price-now]');
      var was = priceHost.querySelector('[data-dv-price-was]');
      if (now) now.textContent = money(variant.price, fmt);
      if (was) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          was.textContent = money(variant.compare_at_price, fmt);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }
    }

    function showMedia(mediaId) {
      if (!mediaId) return;
      var slide = document.getElementById('dv-media-' + mediaId);
      if (!slide) return;
      var main = slide.parentNode;
      main.scrollTo({ left: slide.offsetLeft - main.offsetLeft, behavior: 'auto' });
      $$('[data-dv-gallery-thumbs] .dv-gal__thumb').forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-media-id') === String(mediaId));
      });
    }

    function picValue() {
      var checked = picGroup ? $('.dv-size__input:checked', picGroup) : null;
      return checked ? checked.value : null;
    }

    /* A value of another row that no variant has together with the chosen
       picture value (XXL when only Stitched Edges comes in XXL) is hidden and
       disabled, not marked sold out: there is no such variant to be sold out.
       If it was the selected one it is cleared. A combination that exists but is out of
       stock is left to markAvailability(), which labels it sold out as
       before. */
    function pruneToPicture() {
      if (picIndex < 0) return;
      var chosen = picValue();
      groups.forEach(function (group, gi) {
        if (gi === picIndex) return;
        $$('.dv-size__input', group).forEach(function (input) {
          var exists = chosen === null;
          for (var i = 0; !exists && i < variants.length; i++) {
            if (variants[i].options[picIndex] === chosen && variants[i].options[gi] === input.value) exists = true;
          }
          var label = group.querySelector('label[for="' + input.id + '"]');
          input.hidden = !exists;
          input.disabled = !exists;
          if (label) label.hidden = !exists;
          if (!exists && input.checked) input.checked = false;
        });
      });
    }

    function syncTiles() {
      if (!picTiles.length) return;
      var chosen = picValue();
      picTiles.forEach(function (tile) {
        var on = tile.getAttribute('data-dv-look-value') === chosen;
        tile.classList.toggle('is-current', on);
        tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function replaceUrl(variant) {
      if (window.history && window.history.replaceState && root.getAttribute('data-product-url')) {
        /* On a page opened inside a Men, Women or fandom collection Liquid also
           writes that address (data-dv-context-url). Keeping it here keeps the
           collection in the URL, so the same image set comes back after a
           reload, Back or a shared link. */
        var url = (root.getAttribute('data-dv-context-url') || root.getAttribute('data-product-url')) + '?variant=' + variant.id;
        window.history.replaceState({}, '', url);
      }
    }

    /* A picture value is chosen but no size yet, so there is no single
       variant. The price, the picture and the address still follow the
       picture value, from the variants that match what is chosen: their price
       when they share one, otherwise the lowest of them. The add button is
       left to the code below and pdp-enhance.js, so with no size chosen it
       behaves exactly as on a size-only product. On page load only the price
       is drawn; the address and gallery stay as the page opened. */
    function showPartial(values) {
      if (picIndex < 0 || values[picIndex] === null) return;
      var pool = variants.filter(function (v) {
        for (var j = 0; j < values.length; j++) {
          if (values[j] !== null && v.options[j] !== values[j]) return false;
        }
        return true;
      });
      if (!pool.length) return;
      var lead = pool[0];
      for (var i = 0; i < pool.length; i++) { if (pool[i].available) { lead = pool[i]; break; } }
      var cheapest = pool[0];
      for (var k = 1; k < pool.length; k++) { if (pool[k].price < cheapest.price) cheapest = pool[k]; }
      renderPrice(cheapest);
      if (booting) return;
      if (lead.featured_media) showMedia(lead.featured_media.id);
      idInput.value = lead.id;
      replaceUrl(lead);
    }

    function update() {
      pruneToPicture();
      var values = selectedOptions();
      var variant = match(values);

      $$('.dv-variants__group', root).forEach(function (group, gi) {
        var out = group.querySelector('[data-dv-option-value]');
        if (out) out.textContent = values[gi] || '';
      });

      markAvailability();
      syncTiles();

      if (!variant) {
        showPartial(values);
        if (submit) {
          submit.disabled = true;
          if (submitText) submitText.textContent = strings.unavailable || '';
        }
        return;
      }

      idInput.value = variant.id;
      renderPrice(variant);
      if (variant.featured_media) showMedia(variant.featured_media.id);

      if (submit) {
        submit.disabled = !variant.available;
        if (submitText) submitText.textContent = variant.available ? (strings.add_to_cart || '') : (strings.sold_out || '');
      }

      replaceUrl(variant);
    }

    radios.forEach(function (input) { input.addEventListener('change', update); });

    /* A picture tile selects its value in place: it checks that radio and
       fires the same change event a click on the radio would, so this file
       and pdp-enhance.js both respond exactly as they do to a size. */
    picTiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        var value = tile.getAttribute('data-dv-look-value');
        var target = null;
        $$('.dv-size__input', picGroup).forEach(function (input) {
          if (input.value === value) target = input;
        });
        if (!target || target.checked) return;
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    update();
    booting = false;

    var thumbs = $('[data-dv-gallery-thumbs]', root);
    if (thumbs) {
      thumbs.addEventListener('click', function (event) {
        var thumb = event.target.closest('.dv-gal__thumb');
        if (!thumb) return;
        event.preventDefault();
        showMedia(thumb.getAttribute('data-media-id'));
      });
    }
  }

  /* Shopify product recommendations, fetched through the Section Rendering API.
     Nothing renders unless Shopify actually returns products. */
  function initRecommendations() {
    $$('[data-dv-recommendations]').forEach(function (host) {
      var url = host.getAttribute('data-url');
      if (!url || host.querySelector('.dv-rail')) return;
      var intent = host.getAttribute('data-intent');
      if (intent) url += '&intent=' + encodeURIComponent(intent);

      var run = function () {
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var fresh = parsed.querySelector('[data-dv-recommendations] .dv-container');
            if (fresh) host.appendChild(fresh);
          })
          .catch(function () { /* leave the section empty rather than show a broken state */ });
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.disconnect();
            run();
          });
        }, { rootMargin: '400px' });
        io.observe(host);
      } else {
        run();
      }
    });
  }

  /* ------------------------------------------------------------ fit finder */
  /* Matches the shopper's own numbers against THIS PRODUCT'S size rules, which
     the merchant entered. If nothing matches we say so — we never round to the
     nearest size and present it as a recommendation. */
  function initFitFinder() {
    $$('[data-dv-fitfinder]').forEach(function (root) {
      var rulesNode = $('[data-dv-fit-rules]', root);
      if (!rulesNode) return;

      var rules;
      try { rules = JSON.parse(rulesNode.textContent); } catch (e) { return; }
      if (!Array.isArray(rules) || !rules.length) return;

      var strings = {};
      var sNode = $('[data-dv-fit-strings]', root);
      if (sNode) { try { strings = JSON.parse(sNode.textContent); } catch (e) { strings = {}; } }

      var inputs = {};
      $$('[data-dv-fit]', root).forEach(function (el) { inputs[el.getAttribute('data-dv-fit')] = el; });
      var build = 'Regular';
      var result = $('[data-dv-fit-result]', root);
      var sizeOut = $('[data-dv-fit-size]', root);
      var noteOut = $('[data-dv-fit-note]', root);
      var resetBtn = $('[data-dv-fit-reset]', root);

      function paintValues() {
        Object.keys(inputs).forEach(function (key) {
          var el = inputs[key];
          var out = $('[data-dv-fit-out="' + key + '"]', root);
          if (out) out.textContent = el.value + ' ' + (el.getAttribute('data-unit') || '');
        });
      }

      function inRange(value, range) {
        if (!Array.isArray(range) || range.length < 2) return true;
        return value >= Number(range[0]) && value <= Number(range[1]);
      }

      function match() {
        var h = Number(inputs.height ? inputs.height.value : 0);
        var w = Number(inputs.weight ? inputs.weight.value : 0);
        for (var i = 0; i < rules.length; i++) {
          var rule = rules[i] || {};
          if (!inRange(h, rule.height_cm)) continue;
          if (!inRange(w, rule.weight_kg)) continue;
          if (Array.isArray(rule.build) && rule.build.length && rule.build.indexOf(build) === -1) continue;
          return { rule: rule, index: i };
        }
        return null;
      }

      function show() {
        var hit = match();
        if (!result) return;
        result.hidden = false;
        if (resetBtn) resetBtn.hidden = false;

        if (!hit || !hit.rule.size) {
          if (sizeOut) sizeOut.textContent = '—';
          if (noteOut) noteOut.textContent = strings.no_match || '';
          return;
        }

        if (sizeOut) sizeOut.textContent = hit.rule.size;

        var next = rules[hit.index + 1];
        if (noteOut) {
          noteOut.textContent = (next && next.size)
            ? (strings.alternative || '').replace('%SIZE%', next.size)
            : '';
        }

        /* Remembered on this device only, so the size recommendation block can
           show it again on a return visit. Nothing leaves the browser. */
        try {
          var host = root.closest('[data-dv-product]');
          var pid = host ? host.getAttribute('data-product-url') : 'product';
          window.sessionStorage.setItem('dv:fit:' + pid, hit.rule.size);
        } catch (e) { /* storage unavailable — the result is still on screen */ }

        /* The shopper has just told us their size in their own words. Sharing
           it with the size preference means the collection grid can say
           whether that size is in stock — still their own data, still on their
           own device, still a single size label and nothing else. */
        if (window.DirevulfSize && hit.rule.size) window.DirevulfSize.set(hit.rule.size);

        revealSaved(hit.rule.size, hit.rule);
      }

      /* The merchant's own rule for a size, found by its label. Used to show
         the shopper the reasoning behind the answer instead of asking them to
         take it on trust. */
      function ruleForSize(size) {
        for (var i = 0; i < rules.length; i++) {
          if (rules[i] && String(rules[i].size) === String(size)) return rules[i];
        }
        return null;
      }

      /* A range as the merchant wrote it: "172–180 cm". Anything they left
         blank produces nothing, and its row is not drawn. */
      function rangeText(range, unit) {
        if (!Array.isArray(range) || range.length < 2) return '';
        if (range[0] == null || range[1] == null) return '';
        return String(range[0]) + '–' + String(range[1]) + ' ' + unit;
      }

      function paintWhy(rec, rule) {
        var why = $('[data-dv-sizerec-why]', rec);
        if (!why) return;
        if (!rule) { why.hidden = true; return; }

        var values = {
          height: rangeText(rule.height_cm, 'cm'),
          weight: rangeText(rule.weight_kg, 'kg'),
          build: Array.isArray(rule.build) ? rule.build.join(', ') : ''
        };

        var shown = 0;
        Object.keys(values).forEach(function (key) {
          var row = $('[data-dv-why-row="' + key + '"]', why);
          var out = $('[data-dv-why-value="' + key + '"]', why);
          var text = values[key];
          if (out) out.textContent = text;
          if (row) row.hidden = !text;
          if (text) shown += 1;
        });

        why.hidden = shown === 0;
      }

      function revealSaved(size, rule) {
        var found = rule || ruleForSize(size);
        $$('[data-dv-sizerec]').forEach(function (rec) {
          var wrap = $('[data-dv-sizerec-saved]', rec);
          var out = $('[data-dv-sizerec-size]', rec);
          if (!wrap || !out || !size) return;
          out.textContent = size;
          wrap.hidden = false;
          paintWhy(rec, found);
        });
      }

      $$('[data-dv-fit]', root).forEach(function (el) {
        el.addEventListener('input', paintValues);
      });

      $$('[data-dv-fit-build]', root).forEach(function (button) {
        button.addEventListener('click', function () {
          build = button.getAttribute('data-dv-fit-build');
          $$('[data-dv-fit-build]', root).forEach(function (other) {
            other.setAttribute('aria-pressed', String(other === button));
          });
        });
      });

      var submit = $('[data-dv-fit-submit]', root);
      if (submit) submit.addEventListener('click', show);
      if (resetBtn) {
        resetBtn.addEventListener('click', function () {
          if (result) result.hidden = true;
          resetBtn.hidden = true;
        });
      }

      paintValues();

      /* Restore a previous answer for this product, if there is one. */
      try {
        var host = root.closest('[data-dv-product]');
        var pid = host ? host.getAttribute('data-product-url') : 'product';
        var saved = window.sessionStorage.getItem('dv:fit:' + pid);
        if (saved) revealSaved(saved);
      } catch (e) { /* nothing saved */ }
    });
  }

  function boot() {
    $$('[data-dv-product]').forEach(initProduct);
    initRecommendations();
    initFitFinder();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Re-init after a Theme Editor section reload. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-dv-product]');
    if (root) initProduct(root);
  });
})();
